<template>
  <div class="order-review">
    <div class="row">
      <div class="bg-cl-secondary cartsummary-wrapper">
        <cart-summary :shipping-methods="shippingMethods" :delivery-price="deliveryPrice" />
        <div class="container">
          <div class="row center-xs">
            <div class="col-sm-10 col-md-7 col-xs-12">
              <div class="conditions-shop">
                <base-checkbox
                  class="checkbox-min-with start-xs"
                  id="acceptTermsCheckbox"
                  v-model="orderReview.terms"
                >
                  {{ $t('I agree to') }}
                  <span class="link pointer" @click.prevent="openModal">{{ $t('Terms and conditions') }}</span>
                </base-checkbox>
                <div v-if="!$v.orderReview.terms.required && $v.orderReview.terms.$error" class="col-md-12 col-xs-12 mt10">
                  <p class="bold notif p10 h5 col-md-5 col-sm-8 col-xs-12">
                    <span class="notif-corner"></span>
                    <span class="notif-text">!</span>
                    {{ $t('You must agree to the terms') }}
                  </p>
                </div>
              </div>
              <div class="py20">
                <slot name="placeOrderButton">
                  <google-pay-button v-if="!isDeliveryPriceUnknown && paymentMethod === 'ipay_gpay'" :on-click-validation="buttonValidation"
                                     @payed="payed" @cancel="cancelled" :transaction-info="transactionInfo"
                  />
                  <apple-pay-button v-else-if="!isDeliveryPriceUnknown && paymentMethod === 'ipay_applepay'" :totals="applePayTotals" @payed="payed"
                                    @cancel="cancelled" />
                  <button-full v-else @click.native="beforePlaceOrder"
                               data-testid="orderReviewSubmit" class="place-order-btn"
                               :disabled="isDeliveryPriceUnknown"
                  >
                    <img
                      src="./../../../../assets/icons/icon-6.png"
                      width="22"
                      height="15"
                      alt="Place the order"
                      loading="lazy"
                      fetchpriority="auto"
                    >
                    {{ $t('Place the order') }}
                  </button-full>
                  <div v-if="isDeliveryPriceUnknown" class="align-center mt10">
                    <p class="h5 bold cl-error">{{ $t('Could not calculate the delivery price') }}</p>
                    <mini-button-full data-testid="deliveryPriceRetry" :disabled="isCartLoading"
                                      @click.native="retryDeliveryPrice"
                    >
                      {{ $t('Try again') }}
                    </mini-button-full>
                  </div>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <modal-terms-of-use :showModal="showModal"/>
  </div>
</template>

<script async>
import { required } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'
import { OrderReview } from 'src/modules/checkout/components/OrderReview'
import Composite from '@vue-storefront/core/mixins/composite'

const BaseCheckbox = () => import(/* webpackChunkName: "vsf-base-checkbox" */ 'theme/components/core/blocks/Form/BaseCheckbox');
const ButtonFull = () => import(/* webpackChunkName: "vsf-button-full" */ 'theme/components/theme/ButtonFull');
const MiniButtonFull = () => import(/* webpackChunkName: "vsf-mini-button-full" */ 'theme/components/theme/MiniButtonFull.vue');
const CartSummary = () => import(/* webpackChunkName: "vsf-cart-summary" */ 'theme/components/core/blocks/Checkout/CartSummary');
const ModalTermsOfUse = () => import(/* webpackChunkName: "vsf-modal-term-of-use" */ './ModalTermsOfUse');
const GooglePayButton = () => import(/* webpackChunkName: "vsf-google-pay-button" */ 'src/modules/ipay-payment/components/GooglePayButton');
const ApplePayButton = () => import(/* webpackChunkName: "vsf-apple-pay-button" */ 'src/modules/ipay-payment/components/ApplePayButton');

export default {
  components: {
    BaseCheckbox,
    ButtonFull,
    MiniButtonFull,
    CartSummary,
    ModalTermsOfUse,
    GooglePayButton,
    ApplePayButton
  },
  data () {
    return {
      showModal: false
    }
  },
  props: {
    shippingMethods: {
      type: Array,
      required: false
    },
    isCard: {
      type: Boolean,
      required: true
    },
    shipping: {
      type: Object,
      required: true
    }
  },
  mixins: [OrderReview, Composite],
  validations: {
    orderReview: {
      terms: {
        required
      }
    }
  },
  mounted () {
    let storageContactToReplaceValue = false
    let storageContactToReplace = localStorage.getItem('shop/checkoutFieldValues/contact-to-replace')
    if (storageContactToReplace) {
      storageContactToReplaceValue = JSON.parse(storageContactToReplace)
    }
    this.callMeChanged(storageContactToReplaceValue)
    this.orderReview.contactToReplace = storageContactToReplaceValue;
  },
  methods: {
    ...mapActions({
      initializeWidgetSession: 'ipay-payment-widget/initializeWidgetSession'
    }),
    buttonValidation() {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    cancelled () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('You have canceled the payment'),
        action1: { label: this.$t('OK') }
      })
    },
    payed (paymentData) {
      if (paymentData && (paymentData.paymentMethodData && paymentData.paymentMethodData.tokenizationData)) {
        let paymentToken = paymentData.paymentMethodData.tokenizationData.token
        let token = btoa(paymentToken)
        this.$bus.$emit('gpay-token-received', token)
        this.beforePlaceOrder()
      }
      if (paymentData && paymentData.token) {
        let token = btoa(JSON.stringify(paymentData.token))
        this.$bus.$emit('gpay-token-received', token)
        this.beforePlaceOrder()
      }
    },
    async beforePlaceOrder () {
      if (this.isDeliveryPriceUnknown) return

      let deliveryOrder = this.shippingMethods.find(m => m.method.method_code !== 'pickup')
      if (deliveryOrder && (!this.shipping.longitude || !this.shipping.latitude || !this.shipping.api_details)) {
        this.$bus.$emit('scroll-to-the-address-form');
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('Address is not valid!'),
          action1: {label: this.$t('OK')},
          action2: {
            label: this.$t('Edit'),
            action: () => {
              this.$router.push({name: 'my-shipping-details'}, () => {
                setTimeout(() => {
                  this.$bus.$emit('customer-display-shipping-address-form', {type: 'edit', item: this.shipping})
                }, 100)
              })
            }
          }
        })
        return
      }
      if (this.isCard) {
        if (!this.cards.length) {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: this.$t('You have no added CCs'),
            action1: { label: this.$t('OK') }
          })
          return
        }
        if (!this.defaultCard) {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: this.$t('You have no selected CC'),
            action1: { label: this.$t('OK') }
          })
          return
        }
      }
      this.clearOrderHistory()
      this.placeOrder()
    },
    onSuccess () {
    },
    onFailure (result) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t(result.result),
        action1: { label: this.$t('OK') }
      })
    },
    addComment (payload) {
      this.comment = payload.comment;
    },
    openModal () {
      this.$bus.$emit('modal-toggle', 'modal-terms');
      this.showModal = true;
    },
    ...mapActions({
      clearOrderHistory: 'user/clearOrderHistory'
    })
  },
  computed: {
    isGift () {
      let isGiftPartner = this.shippingMethods.length === 1 && this.shippingMethods.find(el => el.partner === 'gift')
      if (isGiftPartner) {
        this.$bus.$emit('checkout-contact-to-replace-changed', false)
      }
      return isGiftPartner
    },
    showCallMe () {
      let location = this.$store.getters['locations/getActiveLocation']
      return location && location.code !== 'lutsk'
    }
  }
}
</script>

<style lang="scss" scoped>
  .link {
    text-decoration: underline;
  }

  .textarea-box {
    margin-left: 5px;
  }
  .notif {
    position: relative;
    border: 1px solid #e02124;
    padding-left: 35px;
    margin: 0 auto;

    .notif-corner {
      color: #fff;
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 38px 38px 0 0;
      border-color: #e02124 transparent transparent transparent;
    }

    span.notif-text {
      position: absolute;
      left: 8px;
      font-size: 16px;
      top: 3px;
      color: #fff;
    }
  }
  .cartsummary-wrapper{
    width: 100vw;
  }

  .checkbox-min-with {
    min-width: 300px;
  }
</style>
